'use client';
import { getYouTubeVideoId } from '@/lib/link-in-bio/youtube';
import { useState } from 'react';
import { BioDrawer } from './BioDrawer';
import { Button, Image, TextInput, Textarea } from '@mantine/core';
import {
  IconArrowUpRight,
  IconChevronDown,
  IconSearch,
  IconSend,
  IconCheck,
} from '@tabler/icons-react';
import type { BioBlock, BioItem } from '@/lib/link-in-bio/model';
import { withBrandfetchClientId } from '@/lib/brands/logo';
import { BioBlockIcon } from './BioBlockIcon';
import s from './rich-bio.module.css';
import './bio-motion.css';

export function BioPhoto({ src, title }: { src?: string; title: string }) {
  return (
    <Image
      src={withBrandfetchClientId(src) || undefined}
      alt={title}
      className={s.photo}
      fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='450'%3E%3Crect width='600' height='450' fill='%23252528'/%3E%3Cpath d='M270 240l20-25 20 18 25-38 30 45z' fill='%23838388'/%3E%3C/svg%3E"
      loading="lazy"
      referrerPolicy="no-referrer"
    />
  );
}

export function BioCollection({
  block,
  preview,
  track,
}: {
  block: BioBlock;
  preview: boolean;
  track: (id: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const items = block.items ?? [];
  const filtered = items.filter((item) =>
    `${item.title} ${item.description ?? ''}`.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <section className={s.collection} aria-label={block.title}>
      <div className={s.collectionHeading}>
        {/* Marketing previews sit inside another page, so they must not add to its outline. */}
        {preview ? <p>{block.title}</p> : <h2>{block.title}</h2>}
        <span>{items.length}</span>
      </div>
      {block.description && <p className={s.description}>{block.description}</p>}
      {items.length > 6 && (
        <TextInput
          className={s.search}
          aria-label={`Search ${block.title.toLowerCase()}`}
          placeholder="Find something…"
          leftSection={<IconSearch size={16} />}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
        />
      )}
      <CollectionItems
        items={query ? filtered : filtered.slice(0, 6)}
        preview={preview}
        onVisit={() => track(block.id)}
      />
      {!filtered.length && (
        <p role="status" className={s.description}>
          No matches. Try another word.
        </p>
      )}
      {!query && items.length > 6 && (
        <details open={showAll} onToggle={(event) => setShowAll(event.currentTarget.open)}>
          <summary className={s.more}>
            {showAll ? 'Show fewer' : `View all ${items.length}`}{' '}
            <IconChevronDown
              size={16}
              style={{ transform: showAll ? 'rotate(180deg)' : undefined }}
            />
          </summary>
          <CollectionItems
            items={filtered.slice(6)}
            preview={preview}
            onVisit={() => track(block.id)}
          />
        </details>
      )}
    </section>
  );
}

function CollectionItems({
  items,
  preview,
  onVisit,
}: {
  items: BioItem[];
  preview: boolean;
  onVisit: () => void;
}) {
  return (
    <div className={s.grid}>
      {items.map((item) => {
        const body = (
          <>
            <BioPhoto src={item.image} title={item.title} />
            <div className={s.cardCaption}>
              {preview ? <p>{item.title}</p> : <h3>{item.title}</h3>}
              <IconArrowUpRight size={16} aria-hidden />
            </div>
          </>
        );
        return preview ? (
          <div key={item.id} className={s.tile}>
            {body}
          </div>
        ) : (
          <a
            key={item.id}
            href={item.url}
            className={s.tile}
            target="_blank"
            rel="ugc noopener noreferrer"
            onClick={onVisit}
            onAuxClick={(event) => {
              if (event.button === 1) onVisit();
            }}
          >
            {body}
          </a>
        );
      })}
    </div>
  );
}

function youtubeEmbed(url: string) {
  const id = getYouTubeVideoId(url);
  return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
}

export function BioRichLink({
  block,
  preview,
  track,
}: {
  block: BioBlock;
  preview: boolean;
  track: (id: string) => void;
}) {
  const [play, setPlay] = useState(false);
  const embed = youtubeEmbed(block.url);
  const destination = (
    <a
      className={s.visit}
      href={preview ? undefined : block.url}
      target="_blank"
      rel="ugc noopener noreferrer"
      onClick={() => !preview && track(block.id)}
    >
      {block.buttonText || 'Open link'}
      <IconArrowUpRight size={16} />
    </a>
  );
  if (block.display === 'expandable')
    return (
      <BioDrawer
        className={s.expandable}
        heading={
          <>
            <span className={s.glyph}>
              <BioBlockIcon block={block} />
            </span>
            <strong>{block.title}</strong>
          </>
        }
      >
        <div className={s.expanded}>
          {embed && play && !preview ? (
            <iframe
              className={s.video}
              src={embed}
              title={block.title}
              allow="encrypted-media; picture-in-picture"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : block.image ? (
            <BioPhoto src={block.image} title={block.title} />
          ) : null}
          {block.description && <p className={s.description}>{block.description}</p>}
          {embed && !play && !preview && (
            <button className={s.more} onClick={() => setPlay(true)}>
              Play video
            </button>
          )}
          {destination}
        </div>
      </BioDrawer>
    );
  const body = (
    <>
      <BioPhoto src={block.image} title={block.title} />
      <div className={s.largeCaption}>
        <strong>{block.title}</strong>
        {block.description && <p className={s.description}>{block.description}</p>}
        <IconArrowUpRight size={20} />
      </div>
    </>
  );
  return preview ? (
    <div className={s.largeCard}>{body}</div>
  ) : (
    <a
      className={s.largeCard}
      href={block.url}
      target="_blank"
      rel="ugc noopener noreferrer"
      onClick={() => track(block.id)}
      onAuxClick={(event) => {
        if (event.button === 1) track(block.id);
      }}
    >
      {body}
    </a>
  );
}

export function BioMessage({
  block,
  slug,
  preview,
}: {
  block: BioBlock;
  slug: string;
  preview: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState<string>();
  return (
    <BioDrawer
      className={s.message}
      heading={
        <>
          <span className={s.messageIcon}>
            <IconSend size={22} />
          </span>
          <span>
            <strong>{block.title}</strong>
            {block.description && <span className={s.description}>{block.description}</span>}
          </span>
        </>
      }
    >
      {sent ? (
        <div className={s.sent} role="status">
          <IconCheck size={24} />
          <strong>Message sent</strong>
          <p>Thanks for getting in touch.</p>
        </div>
      ) : (
        <form
          className={s.form}
          onSubmit={async (event) => {
            event.preventDefault();
            if (preview) return;
            const form = new FormData(event.currentTarget);
            const attemptId = attempt ?? crypto.randomUUID();
            setAttempt(attemptId);
            setBusy(true);
            setError('');
            try {
              const response = await fetch(`/api/bio/${slug}/messages`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  blockId: block.id,
                  attemptId,
                  name: form.get('name'),
                  email: form.get('email'),
                  message: form.get('message'),
                  website: form.get('website'),
                }),
              });
              const result = await response.json();
              if (!response.ok) throw new Error(result.error || 'Your message could not be sent.');
              setSent(true);
            } catch (e) {
              setError(
                e instanceof Error
                  ? e.message
                  : 'Your message could not be sent. Please try again.',
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          <TextInput label="Your name (optional)" name="name" maxLength={80} autoComplete="name" />
          <TextInput
            label="Your email"
            name="email"
            type="email"
            required
            maxLength={254}
            autoComplete="email"
          />
          <Textarea
            label="Message"
            name="message"
            required
            minLength={3}
            maxLength={3000}
            minRows={4}
          />
          <div hidden aria-hidden>
            <input name="website" tabIndex={-1} autoComplete="off" />
          </div>
          <p className={s.privacy}>
            Your details are shared with the page owner so they can reply.
          </p>
          {error && (
            <p role="alert" className={s.error}>
              {error}
            </p>
          )}
          <Button type="submit" loading={busy} disabled={preview} className={s.send}>
            {block.buttonText || 'Send message'}
            <IconArrowUpRight size={16} />
          </Button>
          {preview && <p className={s.privacy}>Messages can be sent from your published page.</p>}
        </form>
      )}
    </BioDrawer>
  );
}
